import React, { useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useVideo } from 'customHooks/mediaElement';
import RadicalInfoBar from './RadicalInfoBar';
import WordsVideoControl from './WordsVideoControl';
import WordsMovieButtonGroup from './WordsMovieButtonGroup';
import WordsMeaningButtonGroup from './WordsMeaningButtonGroup';
import AudioButton from 'components/common/AudioButton';
import Icon from 'components/Icon';
import GridButtonGroup from './GridButtonGroup';
import useSetState from 'customHooks/setState';
import { mediaType } from 'constants/mediaType';
import { isImageExtension } from 'util/helper';
import { useStore, StoreTypes } from 'context';
import { API } from 'api';
import Bopomofo from 'components/common/Bopomofo';
import { EventBus, EventBusType } from 'events/EventBus';
import { ExtendedContentEvent, ReaderToolsEvent } from 'events/EventTypes';
import { LineType } from 'constants/wordGrid';
import classnames from 'classnames';
import { PinchPanContainer } from 'components/PinchPanContainer';
import * as actionTypes from 'constants/actionTypes';
import styles from './index.module.scss';
import { getOSAndBrowser } from 'util/getOs';
import Repository from 'repositories/Repository';
import { stat } from 'fs';

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { FontSizeType } from 'constants/fontSizeType';

const { ExtendedResourceRepository } = Repository;

const videoPointSetControlStateType = {
  PLAY: 'play',
  PAUSE: 'pause',
  NEXT: 'next',
  REPEAT: 'repeat',
  PREVIOUS: 'previous'
};


const EXTENDTEACH_WORDING_MAP = {
  meaningSet: '字義教學',
  wordsSet: '語詞教學',
  idiomSet: '生字成語',
  glyphSet: '字音字形'
};

const MOVIE_BUTTON_WORDING_MAP = ['筆順教學', '筆畫教學', '綜合教學', '書寫提醒'];
const WordLearningGridComponent = ({
  data,
  wordIndex,
  goToNextWordIndex,
  onRadicalClick = () => { },
  isPausePlayAllWords,
  setPausePlayAllWords
}) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ isShowSubContent }, wordGridDispatch] = useStore(StoreTypes.wordGrid);
  const [{ wordLearningGrid: {
    playbackRate
  }, fontSize }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);

  const [
    {
      Name: word,
      RadicalWord: radical,
      RadicalNumber: radicalNumber,
      AllNumber: allNumber,
      RestNumber: restNumber,
      Phonetic: phonetic,
      WordMovieSet,
      MeaningSet,
      WordsSet,
      IdiomSet,
      GlyphSet
    },
    setDataState
  ] = useSetState(data);

  const [
    {
      videoAutoPlay,
      videoSrc,
      videoPointSet,
      currentPointSetIndex,
      currentVideoIndex,
      isZoomVideo,
      currentWordsMeaningIndex,
      isPlayAllWords,
      gridValue,
      isSwitching,
      videoPointSetControlState
    },
    setState
  ] = useSetState({
    videoAutoPlay: false,
    videoSrc: WordMovieSet[0].Path || '',
    videoPointSet: WordMovieSet[0].MoviePointSet || [],
    currentPointSetIndex: 0,
    currentVideoIndex: 0,
    isZoomVideo: false,
    currentWordsMeaningIndex: null,
    isPlayAllWords: false,
    gridValue: LineType.Dotted,
    isSwitching: false,
    videoPointSetControlState: videoPointSetControlStateType.PAUSE
  });

  const {
    element: video,
    state,
    controls
  } = useVideo(
    <video
      src={`${ExtendedResourceRepository.getDataSrc({
        bookId,
        pathName: videoSrc
      })}#t=0.1`}
      autoPlay={videoAutoPlay}
      playsInline
      crossOrigin="anonymous"
      preload="metadata"
    />
  );

  
  // 20250227 在 ios 手機上 video 的寬度，沒辦法即時 render 正確，所以故意利用 fontSize 來觸發 re-render
  useEffect(() => {
    if (isShowSubContent) return;
    if (state.isLoaded) {
      extendedContentDispatch({
        type: actionTypes.SET_EXTENDED_CONTENT_FONT_SIZE,
        payload: {
          fontSize: FontSizeType.Medium,
        },
      });
    } else {
      extendedContentDispatch({
        type: actionTypes.SET_EXTENDED_CONTENT_FONT_SIZE,
        payload: {
          fontSize: FontSizeType.Large,
        },
      });
    }
  }, [state, isShowSubContent])

  const repeatButtonClickHandler = () => {
    setState({ videoPointSetControlState: videoPointSetControlStateType.REPEAT });
    const lastPoint = Math.max(currentPointSetIndex - 1, 0);
    controls.seek(videoPointSet[lastPoint]);
    controls.play();
  }

  // for 單筆重複
  useEffect(() => {
    if (videoPointSetControlState !== videoPointSetControlStateType.REPEAT) return;
    const endPointTime = videoPointSet[currentPointSetIndex];
    if (state.time >= endPointTime) {
      controls.pause();
    }
  }, [controls, currentPointSetIndex, setState, state.time, videoPointSet, videoPointSetControlState])


  useLayoutEffect(() => {
    // 需求為 延遲播放不跟音檔重疊
    setTimeout(() => {
      !isShowSubContent && !isPausePlayAllWords && isPlayAllWords && controls.play();
      setPausePlayAllWords(false)
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, isPlayAllWords, videoSrc]);

  // for 上下一筆的播放
  useEffect(() => {
    if (
      videoPointSetControlState !== videoPointSetControlStateType.NEXT &&
      videoPointSetControlState !== videoPointSetControlStateType.PREVIOUS
    ) return;
    const endPointTime = videoPointSet[currentPointSetIndex];
    if (state.isPlaying && state.time >= endPointTime) {
      controls.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.time,
    state.isPlaying,
    currentPointSetIndex,
    videoPointSetControlState,
    videoPointSet
  ]);

  // 隨著播放時間 更新 point 的 index
  useEffect(() => {
    // for normal play
    if (
      videoPointSet.length < 1 ||
      !videoPointSetControlState ||
      (videoPointSetControlState !== videoPointSetControlStateType.PLAY &&
        videoPointSetControlState !== videoPointSetControlStateType.PAUSE)
    ) return;
    let pointIndex = currentPointSetIndex;
    let pointTime = videoPointSet[currentPointSetIndex];
    if (state.isPlaying && state.time >= pointTime) {
      pointIndex = Math.min(pointIndex + 1, videoPointSet.length - 1);
      setState({
        currentPointSetIndex: pointIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.time,
    state.isPlaying,
    currentPointSetIndex,
    videoPointSet,
    videoPointSetControlState,
    setState
  ]);

  // 自動播放下一個字
  useEffect(() => {
    if (!state.duration) return;
    if (state.time >= state.duration && isPlayAllWords && !isSwitching) {
      setState({ isSwitching: true });
      goToNextWordIndex();
      // TODO: should implement useVideo onload listener to handle this
      setTimeout(() => {
        setState({ isSwitching: false });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.time,
    state.duration,
    wordIndex,
    isPlayAllWords,
    goToNextWordIndex,
    isSwitching,
    setState
  ]);

  const wordMovieButtonHandler = ({ index, value }) => {
    setState({
      videoAutoPlay: false,
      videoSrc: value,
      currentVideoIndex: index,
      videoPointSet: WordMovieSet[index].MoviePointSet,
      currentPointSetIndex: 1,
      videoPointSetControlState: videoPointSetControlStateType.PLAY
    });
  };

  const meaningButtonHandler = useCallback(({ type }) => {
    controls.pause();

    wordGridDispatch({
      type: actionTypes.SET_SUBCONTENT_SHOW,
      payload: {
        isShowSubContent: true,
        extensionType: type
      }
    })

  }, [controls, wordGridDispatch]);

  const pointSetChangeHandler = useCallback(
    type => {
      let pointIndex = currentPointSetIndex;
      if (type === mediaType.PREVIOUS) {
        pointIndex = Math.max(pointIndex - 1, 0);
      } else {
        pointIndex = (pointIndex + 1 >= videoPointSet.length) ? 0 : pointIndex + 1;
      }

      controls.seek(videoPointSet[Math.max(pointIndex - 1, 0)]);
      controls.play();
      setState({
        currentPointSetIndex: pointIndex,
        videoPointSetControlState: type === mediaType.PREVIOUS ? videoPointSetControlStateType.PREVIOUS : videoPointSetControlStateType.NEXT
      });
    },
    [currentPointSetIndex, controls, videoPointSet, setState]
  );

  const playbackRateChangeHandler = playbackRate => {
    EventBus.emit({
      event: ExtendedContentEvent.SetWordLearningGridPlaybackRateEvent,
      payload: { playbackRate }
    })
  }

  useEffect(() => {
    controls.playbackRate(playbackRate);
  }, [controls, playbackRate, videoSrc])

  const play = useCallback(() => {
    state.isPlaying ? controls.pause() : controls.play();
    setState({ videoPointSetControlState: videoPointSetControlStateType[state.isPlaying ? 'PLAY' : 'PAUSE'] });
  }, [controls, setState, state.isPlaying]);

  useEffect(() => {

    const extensionData = {
      meaningSet: data.MeaningSet,
      wordsSet: data.WordsSet,
      idiomSet: data.IdiomSet,
      glyphSet: data.GlyphSet,
    };
    setDataState(data);

    let tempCurrentVideoIndex = currentVideoIndex;
    if(currentVideoIndex > WordMovieSet.length - 1) {
      tempCurrentVideoIndex = 0;
    }

    setState({
      videoAutoPlay: false,
      videoSrc: WordMovieSet[tempCurrentVideoIndex].Path || '',
      videoPointSet: WordMovieSet[tempCurrentVideoIndex].MoviePointSet || [],
      currentPointSetIndex: 0,
      currentVideoIndex: tempCurrentVideoIndex,
      videoPointSetControlState: videoPointSetControlStateType.PAUSE,
      audioSrc: '',
      currentWordsMeaningIndex
    });
    wordGridDispatch({
      type: actionTypes.SET_EXTENSION_DATA,
      payload: {
        extensionData
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [WordMovieSet, setState, data, setDataState, isPlayAllWords, wordIndex]);

  const onPlayAllWordsClick = status => {
    setState({ isPlayAllWords: status });
  };

  const onRadicalClickHandler = useCallback(radicalPinyin => {
    controls.pause();
    controls.seek(0);
    onRadicalClick(radicalPinyin);
  }, [controls, onRadicalClick])

  const toggleZoomVideoHandler = useCallback(() => {
    controls.pause();
    controls.seek(0);
    setState({ isZoomVideo: !isZoomVideo })
  }, [controls, isZoomVideo, setState])

  return (
    <>
      <div className={classnames(styles.center, styles.images)}>
        <div className={classnames(styles.videoContainer, {
          [styles.fullSize]: isZoomVideo
        })}>
          <div className={styles.gridWrapper}>
            <div className={styles.grid}>
              <div className={classnames(styles.video, [styles[gridValue]])}>
                {video}
              </div>
              {phonetic && isImageExtension(phonetic) ? (
                <div className={styles.phonetic}>
                  <img
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: phonetic
                    })}
                    alt=""
                  />
                </div>
              ) : (<Bopomofo className={styles.bopomofo} zhuyin={phonetic} />)}
              <div className={styles.zoomIcon} onClick={toggleZoomVideoHandler}>
                <Icon type="text" name={isZoomVideo ? 'searchMinus' : 'searchPlus'} />
              </div>
            </div>
          </div>
        </div>
        {
          !isZoomVideo && (
            <RadicalInfoBar
              radical={radical}
              radicalNumber={radicalNumber}
              allNumber={allNumber}
              restNumber={restNumber}
              onRadicalClick={onRadicalClickHandler}
            />
          )
        }
      </div>
      <div className={styles.right}>
        <section>
          <div className={styles.title}>
            <span>教學方式</span>
          </div>
          <WordsMovieButtonGroup
            isPlaying={state.isPlaying}
            play={play}
            wordingMap={MOVIE_BUTTON_WORDING_MAP}
            data={WordMovieSet}
            activeIndex={currentVideoIndex}
            onChange={wordMovieButtonHandler}
          />
          <WordsVideoControl
            previousButtonClick={() =>
              pointSetChangeHandler(mediaType.PREVIOUS)
            }
            previousButtonDisabled={
              currentPointSetIndex === 0 || videoPointSet.length < 1
            }
            nextButtonClick={() => pointSetChangeHandler(mediaType.NEXT)}
            nextButtonDisabled={
              videoPointSet.length < 1
            }
            repeatDisabled={state.isPlaying || currentPointSetIndex < 1}
            repeatButtonClick={() => repeatButtonClickHandler()}
            playbackRate={playbackRate}
            onChangePlaybackRate={playbackRateChangeHandler}
            onPlayAllWordsClick={onPlayAllWordsClick}
          />
        </section>
        <section className={styles.extendSection}>
          <div className={styles.title}>
            <span>延伸教學</span>
          </div>
          <WordsMeaningButtonGroup
            className={styles.extendTeach}
            onChange={meaningButtonHandler}
            wordingMap={EXTENDTEACH_WORDING_MAP}
            data={{
              meaningSet: MeaningSet,
              wordsSet: WordsSet,
              idiomSet: IdiomSet,
              glyphSet: GlyphSet
            }}
          />
        </section>
        <section className={styles.gridSection}>
          <div className={styles.title}>
            <span>十字格線</span>
          </div>
          <GridButtonGroup onChange={value => setState({ gridValue: value })} value={gridValue} />
        </section>
      </div>
    </>
  );
};

const WordLearningGrid = ({ dataList = [], defaultWord }) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ word, isShowSubContent, isShowRadicalContent, isShowRadicalProgressModal }, wordGridDispatch] = useStore(StoreTypes.wordGrid);
  const [
    { list, currentWordIndex, isAudioAutoPlay, isPausePlayAllWords },
    setState
  ] = useSetState({
    list: [],
    currentWordIndex: 0,
    isAudioAutoPlay: true,
    isPausePlayAllWords: false
  });

  useEffect(() => {
    Promise.all(
      dataList.map(word =>
        ExtendedResourceRepository.getJSONContent({
          bookId,
          pathName: word.Path
        })
      )
    ).then(list => {
      wordGridDispatch({
        type: actionTypes.SET_WORDS_DATA,
        payload: {
          data: list
        }
      })
      setState({ list: list.filter(item => item) });
    });
  }, [bookId, dataList, setState, wordGridDispatch]);

  const goToNextWordIndex = useCallback(() => {
    const maxIndex = dataList.length - 1;
    const nextIndex = currentWordIndex + 1;
    if (maxIndex >= nextIndex) {
      const wordIndex = Math.min(nextIndex, maxIndex);
      wordGridDispatch({
        type: actionTypes.SET_WORDS_VALUE,
        payload: {
          word: list[wordIndex].Name
        }
      })
    }
  }, [dataList.length, currentWordIndex, wordGridDispatch, list]);

  const onRadicalClick = useCallback(
    async radicalPinyin => {
      const url = await ExtendedResourceRepository.getDataSrc({
        bookId,
        pathName: `word/部首/${radicalPinyin}/${radicalPinyin}.json`
      });
      const data = await API.getJSON(url);
      if (data) {
        // setState({ isRadicalShow: true, radicalData: data });

        wordGridDispatch({
          type: actionTypes.SET_RADICAL_CONTENT_SHOW,
          payload: {
            isShowRadicalContent: true,
            radicalData: data
          }
        })
      }
    },
    [bookId, wordGridDispatch]
  );

  useEffect(() => {
    const tempWord = word || defaultWord;
    if (!tempWord) return;
    const index = dataList.findIndex(item => {
      return item.Name === tempWord;
    });
    setState({ currentWordIndex: index > -1 ? index : 0 });
  }, [dataList, setState, word, defaultWord]);

  useEffect(() => {
    const eventBusType = EventBusType.ExtendedContent;
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ClickEraseAllEvent
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWordIndex, isShowSubContent, isShowRadicalContent, isShowRadicalProgressModal])

  useEffect(() => {
    if (isShowSubContent) {
      setState({ isAudioAutoPlay: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowSubContent])

  // 顯示部首內容時關閉側邊清單的自動播放，避免搶奪播放權
  useEffect(() => {
    if (isShowRadicalContent) {
      setState({ isAudioAutoPlay: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowRadicalContent])

  const setPausePlayAllWords = status => {
    setState({ isPausePlayAllWords: status })
  }

  const wordClickHandler = word => {
    wordGridDispatch({
      type: actionTypes.SET_WORDS_VALUE,
      payload: {
        word
      }
    })

    setPausePlayAllWords(true)
  }


  return (
    <>
      <PinchPanContainer>
        <div
          className={classnames(styles.wordLearningGrid, {
            [styles.notMac]:
              getOSAndBrowser() !== "Mac",
          })}
        >
          <div className={styles.left}>
            <div className={styles.wordList}>
              {dataList.map((item, index) => (
                <div
                  key={item.Name}
                  className={classnames(
                    styles.word,
                    {
                      [styles.active]:
                        index ===
                        currentWordIndex,
                      [styles.optional]: !!item.Optional,
                    }
                  )}
                  onClick={() =>
                    wordClickHandler(item.Name)
                  }
                >
                  {list[index] && (
                    <AudioButton
                      autoPlay={
                        index ===
                        currentWordIndex &&
                        isAudioAutoPlay
                      }
                      src={`${ExtendedResourceRepository.getDataSrc(
                        {
                          bookId,
                          pathName:
                            list[index]
                              .WordAudioPath,
                        }
                      )}`}
                    >
                      {item.Name}
                    </AudioButton>
                  )}
                </div>
              ))}
            </div>
          </div>
          {list && list.length > 0 && (
            <WordLearningGridComponent
              data={list[currentWordIndex]}
              wordIndex={currentWordIndex}
              goToNextWordIndex={goToNextWordIndex}
              onRadicalClick={onRadicalClick}
              isPausePlayAllWords={
                isPausePlayAllWords
              }
              setPausePlayAllWords={
                setPausePlayAllWords
              }
            />
          )}
        </div>
      </PinchPanContainer>
    </>
  );
};

export default WordLearningGrid;
